import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SKILLS } from '../../domain/skills.js';
import { consolidatedAssessment } from '../../lib/assessmentHistory.js';
import { normalizarHabilidadeId } from '../../lib/skillAliases.js';
import { formatDate, generateId, habilidadeLabels, todayISO } from '../../lib/utils.js';
import { caseStudyForStudent } from '../case-study/case-study.js';
import { buildGoalSuggestion, createLinkedGoal, goalTraceability } from './goal-links.js';

function prioritySkills(assessment = {}) {
  const valid = new Set(SKILLS.map((item) => item.id));
  const scores = new Map();
  Object.entries(assessment?.pontuacoes || {}).forEach(([rawId, rawValue]) => {
    const id = normalizarHabilidadeId(rawId);
    const value = Number(rawValue);
    if (!valid.has(id) || !Number.isFinite(value)) return;
    scores.set(id, Math.min(scores.get(id) ?? 100, value));
  });
  return [...scores.entries()].sort((a, b) => a[1] - b[1]).map(([id]) => id);
}

export function StudentPei({ student, data, setData, onSave }) {
  const pei = (data.peis || []).find((item) => item.studentId === student.id)
    || { id: generateId('pei'), studentId: student.id, criadoEm: todayISO(), ultimaRevisao: todayISO(), metas: [] };
  const assessment = consolidatedAssessment(data.assessments || [], student.id);
  const caseStudy = caseStudyForStudent(data.caseStudies, student.id);
  const logs = (data.logs || []).filter((log) => log.studentId === student.id);
  const suggestedSkills = useMemo(() => prioritySkills(assessment), [assessment]);
  const [description, setDescription] = useState('');
  const [skill, setSkill] = useState(() => suggestedSkills[0] || 'soc_interacao');
  const barrier = caseStudy.blocks.barriers.summary;
  const suggestion = buildGoalSuggestion({ skillId: skill, barrier });

  function addGoal(event) {
    event.preventDefault();
    const today = todayISO();
    const goal = createLinkedGoal({
      id: generateId('meta'),
      description,
      skillId: skill,
      caseStudy,
      assessment,
      today
    });
    const nextPei = { ...pei, ultimaRevisao: today, metas: [...pei.metas, goal] };
    const next = { ...data, peis: [...(data.peis || []).filter((item) => item.studentId !== student.id), nextPei] };
    setData(next);
    onSave?.(next);
    setDescription('');
  }

  return <section className='card student-pei'>
    <h2>PEI/PAEE</h2>
    <p className='hint'>Crie a meta e escolha a habilidade. O sistema liga automaticamente a barreira, a linha de base, as atividades e as evidências já registradas.</p>
    {!barrier && <p className='goal-link-alert'>O estudo de caso ainda não tem uma barreira prioritária. A meta pode ser criada agora e será associada à barreira atual quando ela for registrada.</p>}
    {barrier && <aside className='goal-barrier-preview'><b>Barreira usada como origem</b><span>{barrier}</span></aside>}
    <form className='form goal-quick-form' onSubmit={addGoal}>
      <label>Nova meta observável
        <textarea required value={description} onChange={(event) => setDescription(event.target.value)} placeholder='Descreva o desempenho esperado em situação real.' />
      </label>
      <button type='button' className='goal-suggestion-button' onClick={() => setDescription(suggestion)}>Usar sugestão editável</button>
      <label>Habilidade-alvo
        <select value={skill} onChange={(event) => setSkill(event.target.value)}>{SKILLS.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}</select>
      </label>
      <small className='hint'>Linha de base: avaliação mais recente · alvo: pelo menos 20 pontos de avanço · primeira revisão: 90 dias. Esses vínculos são preenchidos automaticamente para evitar nova digitação.</small>
      <button>Adicionar meta</button>
    </form>
    <div className='actions'><Link className='action-link' to={`/aluno/${student.id}/pei-smart`}>Abrir acompanhamento das metas</Link></div>
    <div className='goal-link-list'>{pei.metas.map((goal) => {
      const trace = goalTraceability(goal, { caseStudy, logs });
      const evidence = trace.evidence.items[0];
      return <article className='goal-link-card' key={goal.id}>
        <div><span>Barreira</span><p>{trace.barrier || 'Ainda não registrada'}</p><small>{trace.barrierSource}</small></div>
        <div><span>Habilidade-alvo</span><p>{trace.skills.map((id) => habilidadeLabels[id] || id).join(', ') || '-'}</p><small>Linha de base {goal.linha_base ?? '-'} · alvo {goal.alvo_numerico ?? '-'}%</small></div>
        <div><span>Meta observável</span><p><b>{goal.descricao}</b></p></div>
        <div><span>Atividade aplicada</span><p>{trace.evidence.activities.join(', ') || 'Nenhuma atividade relacionada ainda'}</p></div>
        <div><span>Evidência mais recente</span><p>{evidence ? `${formatDate(evidence.date)} — ${evidence.result}` : 'Aguardando atendimento relacionado'}</p>{trace.evidence.alert && <small className='goal-warning'>{trace.evidence.alert}</small>}</div>
        <div><span>Próxima revisão</span><p>{goal.prazo ? formatDate(goal.prazo) : 'Não definida'}</p></div>
      </article>;
    })}</div>
  </section>;
}
